export interface QueueItem<T = any> {
  id: string;
  data: T;
  retryCount?: number;
  createdAt?: number;
}

export interface BufferQueueOptions {
  maxQueueSize?: number;
}

const DEFAULT_MAX_QUEUE_SIZE = 200;

/**
 * 内存缓冲队列
 * 支持容量上限与先进先出（FIFO）淘汰保护
 */
export class BufferQueue<T = any> {
  private queue: QueueItem<T>[] = [];
  private readonly maxSize: number;

  constructor(options?: BufferQueueOptions) {
    this.maxSize = options?.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE;
  }

  public enqueue(item: QueueItem<T>): void {
    if (this.queue.length >= this.maxSize) {
      // 队列溢出时淘汰最早的元素，防止无界膨胀
      this.queue.shift();
    }
    this.queue.push({
      ...item,
      retryCount: item.retryCount ?? 0,
      createdAt: item.createdAt ?? Date.now(),
    });
  }

  public peek(count: number): QueueItem<T>[] {
    return this.queue.slice(0, count);
  }

  public dequeue(count: number): QueueItem<T>[] {
    return this.queue.splice(0, count);
  }

  /** 按入队后的对象身份确认，避免发送期间 FIFO 淘汰导致位置或 ID 冲突。 */
  public remove(items: readonly QueueItem<T>[]): void {
    const acknowledged = new Set(items);
    this.queue = this.queue.filter((item) => !acknowledged.has(item));
  }

  public size(): number {
    return this.queue.length;
  }

  public clear(): void {
    this.queue = [];
  }

  public getAll(): QueueItem<T>[] {
    return [...this.queue];
  }
}
